import { VISUALIZATION_TYPE, type DashboardWidget } from "../types/core"

/**
 * Columns in the dashboard grid. Chosen so every selectable width is a whole
 * number of columns and each has a complement that fills the rest of a row —
 * a quarter beside three quarters, a third beside two thirds, two halves.
 */
export const DASHBOARD_GRID_COLUMNS = 48

/** Height of a single grid row and the gutter between widgets, in pixels. */
export const DASHBOARD_ROW_HEIGHT_PX = 12
export const DASHBOARD_GUTTER_PX = 12

type WidgetWidthKey =
  | "quarter"
  | "third"
  | "half"
  | "twoThirds"
  | "threeQuarters"
  | "full"

interface WidgetWidthOption {
  key: WidgetWidthKey
  label: string
  columns: number
}

const NARROWEST_WIDTH_COLUMNS = DASHBOARD_GRID_COLUMNS / 4

/** The only widths a widget may take, smallest first. */
const WIDGET_WIDTH_OPTIONS: readonly WidgetWidthOption[] = [
  { key: "quarter", label: "Quarter", columns: NARROWEST_WIDTH_COLUMNS },
  { key: "third", label: "Third", columns: DASHBOARD_GRID_COLUMNS / 3 },
  { key: "half", label: "Half", columns: DASHBOARD_GRID_COLUMNS / 2 },
  {
    key: "twoThirds",
    label: "Two thirds",
    columns: (DASHBOARD_GRID_COLUMNS / 3) * 2,
  },
  {
    key: "threeQuarters",
    label: "Three quarters",
    columns: NARROWEST_WIDTH_COLUMNS * 3,
  },
  { key: "full", label: "Full width", columns: DASHBOARD_GRID_COLUMNS },
]

/** How many cards fit in one row, given a quarter is as narrow as they go. */
export const MAX_ROW_WIDGETS = DASHBOARD_GRID_COLUMNS / NARROWEST_WIDTH_COLUMNS

interface WidgetSizeRule {
  /** Narrowest width the widget renders acceptably at. */
  minColumns: number
  /** Width a newly added widget starts at. */
  defaultColumns: number
  /** Fixed height, in grid rows. Heights are never user-controlled. */
  rows: number
}

const DEFAULT_SIZE_RULE: WidgetSizeRule = {
  minColumns: 16,
  defaultColumns: 24,
  rows: 13,
}

const WIDGET_SIZE_RULES: Record<string, WidgetSizeRule> = {
  github_job_receipts: { minColumns: 24, defaultColumns: 24, rows: 15 },
  chart: { minColumns: 12, defaultColumns: 24, rows: 13 },
  table: { minColumns: 24, defaultColumns: DASHBOARD_GRID_COLUMNS, rows: 15 },
  text: { minColumns: 12, defaultColumns: DASHBOARD_GRID_COLUMNS, rows: 2 },
  assignee_call_volume: { minColumns: 24, defaultColumns: 24, rows: 15 },
  assignee_call_history: {
    minColumns: 24,
    defaultColumns: DASHBOARD_GRID_COLUMNS,
    rows: 15,
  },
}

/**
 * Chart visualizations whose sizing differs from a plotted chart. A statistic
 * is a single number, so it stays short and may sit in a quarter row.
 */
const CHART_VISUALIZATION_SIZE_RULES: Record<string, WidgetSizeRule> = {
  [VISUALIZATION_TYPE.STATISTIC]: {
    minColumns: 12,
    defaultColumns: 12,
    rows: 7,
  },
}

function getWidgetSizeRule(widget: DashboardWidget): WidgetSizeRule {
  if (widget.config?.receiptSource === "github_job_receipts") {
    return WIDGET_SIZE_RULES.github_job_receipts
  }
  if (widget.type === "chart" && widget.config?.visualizationType) {
    const visualizationRule =
      CHART_VISUALIZATION_SIZE_RULES[widget.config.visualizationType]
    if (visualizationRule) {
      return visualizationRule
    }
  }

  return WIDGET_SIZE_RULES[widget.type] ?? DEFAULT_SIZE_RULE
}

/** Height in grid rows. Derived from the widget, never from stored state. */
export function getWidgetRowSpan(widget: DashboardWidget): number {
  return getWidgetSizeRule(widget).rows
}

/** Rendered height in pixels, including the gutters the rows span across. */
export function getWidgetHeightPx(widget: DashboardWidget): number {
  const rows = getWidgetRowSpan(widget)
  return rows * DASHBOARD_ROW_HEIGHT_PX + (rows - 1) * DASHBOARD_GUTTER_PX
}

/** Width options the widget is allowed to take, smallest first. */
export function getWidgetWidthOptions(
  widget: DashboardWidget,
): WidgetWidthOption[] {
  const { minColumns } = getWidgetSizeRule(widget)
  return WIDGET_WIDTH_OPTIONS.filter((option) => option.columns >= minColumns)
}

/** Width a newly added or cloned widget starts at. */
function getDefaultWidgetWidth(widget: DashboardWidget): number {
  return getWidgetSizeRule(widget).defaultColumns
}

/**
 * Coerces an arbitrary column count to the nearest allowed width for the
 * widget, so layouts saved before these rules existed still open cleanly.
 */
export function snapWidgetWidth(
  widget: DashboardWidget,
  columns: number | null | undefined,
): number {
  const options = getWidgetWidthOptions(widget)
  const fallback = getDefaultWidgetWidth(widget)
  const narrowest = options[0]?.columns ?? fallback

  if (typeof columns !== "number" || !Number.isFinite(columns)) {
    return fallback
  }

  const target = Math.max(columns, narrowest)

  return options.reduce((closest, option) => {
    const optionDistance = Math.abs(option.columns - target)
    const closestDistance = Math.abs(closest - target)
    return optionDistance < closestDistance ? option.columns : closest
  }, narrowest)
}

/** The width the user asked for, snapped to an allowed option. */
export function getRequestedWidgetWidth(widget: DashboardWidget): number {
  return snapWidgetWidth(widget, widget.grid_w)
}
